import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import DictationInput from '../components/DictationInput';
import ToneSelector from '../components/ToneSelector';

import { Sparkles, Loader2 } from 'lucide-react';
import { storeReflection } from '../lib/aiReflections';

const BIO_QUESTIONS = [
  {
    key: 'strength_view',
    label: 'If someone who loves you described you at your best, what would they say about you?'
  },
  {
    key: 'proud_moment',
    label: 'Describe an accomplishment you are proud of. What strengths helped you make it happen?'
  },
  {
    key: 'resilience',
    label: 'Share a time you overcame something hard. What did you learn about yourself?'
  },
  {
    key: 'values',
    label: 'What positive qualities do you want to be known for in your community right now?'
  },
  {
    key: 'goals',
    label: 'What goals are you working toward today, and how do they align with who you want to become?'
  }
];

export default function BioPage() {
  const { user, axiosAuth } = useAuth();
  const [bioAnswers, setBioAnswers] = useState(() => BIO_QUESTIONS.reduce((acc, q) => ({ ...acc, [q.key]: '' }), {}));
  const [bioOriginals, setBioOriginals] = useState(() => BIO_QUESTIONS.reduce((acc, q) => ({ ...acc, [q.key]: '' }), {}));
  const [interests, setInterests] = useState('');
  const [bio, setBio] = useState('');
  const [generating, setGenerating] = useState(false);
  const [existingBio, setExistingBio] = useState('');
  const api = axiosAuth();

  useEffect(() => {
    api.get(`/users/${user.id}/bio`).then(r => {
      setExistingBio(r.data.bio || '');
      if (r.data.interests?.length) setInterests(r.data.interests.join(', '));
      const intake = r.data.bio_intake || {};
      if (intake && Object.keys(intake).length > 0) {
        setBioAnswers(prev => {
          const updated = { ...prev };
          BIO_QUESTIONS.forEach(q => {
            if (intake[q.key]) updated[q.key] = intake[q.key];
          });
          return updated;
        });
        setBioOriginals(prev => {
          const updated = { ...prev };
          BIO_QUESTIONS.forEach(q => {
            if (intake[q.key]) updated[q.key] = intake[q.key];
          });
          return updated;
        });
      }
    }).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    const combined = BIO_QUESTIONS.map(q => `${q.label}\n${bioAnswers[q.key] || ''}`).join('\n\n');
    if (!combined.trim()) return toast.error('Please answer at least one question');
    setGenerating(true);
    try {
      const interestList = interests.split(/,|\n|;/).map(i => i.trim()).filter(Boolean);
      const bioType = user.role === 'peer_specialist' ? 'specialist' : 'client';
      const res = await api.post('/ai/generate-bio', {
        spoken_text: combined.trim(),
        bio_type: bioType,
        interests: interestList,
        bio_intake: bioAnswers
      });
      setBio(res.data.bio);
      setExistingBio(res.data.bio);
      toast.success('Bio generated and saved!');

      await Promise.all(BIO_QUESTIONS.map(q => (
        storeReflection(api, {
          original_text: bioOriginals[q.key] || bioAnswers[q.key],
          refined_text: bioAnswers[q.key],
          context: 'bio_intake',
          task_name: q.label,
          task_description: 'Bio intake reflection',
          category: 'identity',
          source: 'bio_intake'
        })
      )));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate bio');
    } finally {
      setGenerating(false);
    }
  };

  const isClient = user.role === 'client' || user.role === 'housing_resident';

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="bio-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
          {isClient ? 'My Story' : 'My Peer Support Profile'}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {isClient
            ? 'Answer a few questions in your own words. We will build a warm bio and personalized prompts.'
            : 'Share your approach, experience, and who you serve'}
        </p>
      </div>

      {existingBio && (
        <Card className="border-0 shadow-sm rounded-2xl bg-teal-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Current Bio</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed text-foreground/80">{existingBio}</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
            {existingBio ? 'Update Your Bio' : 'Create Your Bio'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {isClient ? (
            <div className="space-y-4">
              {BIO_QUESTIONS.map((q, idx) => (
                <div key={q.key} className="space-y-2">
                  <Label className="text-sm font-medium">{idx + 1}. {q.label}</Label>
                  <DictationInput
                    value={bioAnswers[q.key]}
                    onChange={(cleaned, original) => {
                      setBioAnswers(prev => ({ ...prev, [q.key]: cleaned }));
                      setBioOriginals(prev => ({ ...prev, [q.key]: original || cleaned }));
                    }}
                    rows={3}
                    placeholder="Speak or type your response..."
                    promptContext={{
                      context: 'bio_intake',
                      taskName: q.label,
                      taskDescription: 'Strength-based bio reflection to capture positive identity and goals',
                      category: 'identity'
                    }}
                    data-testid={`bio-intake-${q.key}`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Describe your approach, the communities you serve, and your philosophy
              </Label>
              <DictationInput
                value={bioAnswers.goals}
                onChange={(cleaned, original) => {
                  setBioAnswers(prev => ({ ...prev, goals: cleaned }));
                  setBioOriginals(prev => ({ ...prev, goals: original || cleaned }));
                }}
                rows={5}
                placeholder="I focus on helping people who... My approach is... I believe in..."
                data-testid="bio-text-input"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-sm font-medium">What interests and passions give you energy right now?</Label>
            <DictationInput
              value={interests}
              onChange={(cleaned) => setInterests(cleaned)}
              placeholder="Share the hobbies, topics, or activities that light you up"
              rows={2}
              data-testid="bio-interests-input"
            />
            <p className="text-xs text-muted-foreground">These will be woven into your bio to help others connect with you</p>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2 h-11"
            data-testid="generate-bio-button"
          >
            {generating ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><Sparkles size={16} /> Generate My Bio</>}
          </Button>

          {bio && (
            <div className="bg-muted/30 rounded-xl p-4 border border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Generated Bio Preview</p>
              <p className="text-sm leading-relaxed">{bio}</p>
            </div>
          )}

          <ToneSelector
            text={BIO_QUESTIONS.map(q => bioAnswers[q.key]).join('\n')}
            context={isClient ? 'bio_client' : 'bio_specialist'}
            onSelect={(text) => { setBio(text); setExistingBio(text); toast.success('Bio saved!'); }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
